import type { TagDraft } from 'redux/modules/tags.types';
import type { Tag } from 'types/tag';

export type TagStatusFilter = 'active' | 'archived' | 'all';
export type TagBulkOperation = 'archive' | 'restore' | 'delete' | 'move';
export const matchesTagStatus = (tag: Tag | TagDraft, status: TagStatusFilter) =>
  status === 'all' || (status === 'active' ? tag.active !== false : tag.active === false);

// Retain a list as context when one of its children matches the view.
export function filterTagTree(
  tags: (Tag | TagDraft)[],
  status: TagStatusFilter,
  search: string
): (Tag | TagDraft)[] {
  const query = search.trim().toLowerCase();
  return tags.flatMap(tag => {
    if (!tag.id) return [tag];
    const nameMatches = tag.name?.toLowerCase().includes(query) ?? false;
    const children = (('children' in tag && tag.children) || []).filter(
      child =>
        !child.id ||
        (matchesTagStatus(child, status) &&
          (nameMatches || child.name.toLowerCase().includes(query)))
    );
    return (matchesTagStatus(tag, status) && nameMatches) || children.length
      ? [{ ...tag, children }]
      : [];
  });
}

export function visibleTagIds(tags: Tag[], status: TagStatusFilter, search: string): number[] {
  const query = search.trim().toLowerCase();
  return tags.flatMap(tag => {
    const nameMatches = tag.name.toLowerCase().includes(query);
    return [
      ...(nameMatches && matchesTagStatus(tag, status) ? [tag.id] : []),
      ...(tag.children ?? [])
        .filter(
          child =>
            matchesTagStatus(child, status) &&
            (nameMatches || child.name.toLowerCase().includes(query))
        )
        .map(child => child.id),
    ];
  });
}

export function affectedTagIds(
  tags: Tag[],
  selectedIds: number[],
  operation: TagBulkOperation
): number[] {
  const selected = new Set(selectedIds);
  return [
    ...new Set(
      tags.flatMap(tag => {
        const children = tag.children ?? [];
        const affected =
          selected.has(tag.id) && ['archive', 'delete'].includes(operation)
            ? [tag, ...children]
            : [tag, ...children].filter(tag => selected.has(tag.id));
        return affected.filter(tag => operation !== 'archive' || tag.active).map(tag => tag.id);
      })
    ),
  ];
}
